import { Box, ButtonBase } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

export interface ChoiceOption {
  value: string | number;
  label: string;
}

export type ChoiceToggleSize = "default" | "small";

interface Props {
  value: string | number;
  onChange: (value: string | number) => void;
  options: ChoiceOption[];
  disabled?: boolean;
  /**
   * `default` reparte el ancho disponible entre las opciones; `small` las ajusta al texto,
   * para cuando el toggle acompaña a otro contenido en lugar de ocupar la fila completa.
   */
  size?: ChoiceToggleSize;
  /**
   * `cotizador`: los chips de plazo de los cotizadores v2 (antes `ChoiceChipsCotizador`), con el override
   * de Flux: la opción elegida se marca con `primary.accent` y las opciones se parten en filas.
   */
  variant?: "default" | "cotizador";
  /** Prefijo del `data-testid` de cada opción (`${testId}-${value}`). */
  testId?: string;
}

const SIZES = {
  default: { flex: 1, padding: "14px 18px", fontSize: 14 },
  small: { flex: "0 0 auto", padding: "14px 18px", fontSize: 14 },
} as const;

const defaultSx = (selected: boolean, size: ChoiceToggleSize): SxProps<Theme> => ({
  flex: SIZES[size].flex,
  justifyContent: "center",
  border: "1.5px solid",
  borderColor: selected ? "primary.main" : "divider",
  borderRadius: "8px",
  padding: SIZES[size].padding,
  fontSize: SIZES[size].fontSize,
  fontWeight: selected ? 600 : 500,
  color: "primary.dark",
  bgcolor: selected ? "primary.base" : "background.paper",
  boxShadow: selected ? (theme) => `0 0 0 1px ${theme.palette.primary.main} inset` : "none",
  transition: "border-color .15s ease, background-color .15s ease, color .15s ease",
  "&:hover": {
    borderColor: "primary.main",
    bgcolor: "primary.base",
    color: selected ? "primary.dark" : "primary.accent",
  },
  // Deshabilitado: la opción elegida sigue siendo legible pero atenuada,
  // el resto toma el mismo fondo gris que los inputs `lendia_input`.
  "&.Mui-disabled": {
    borderColor: selected ? "primary.main" : "divider",
    bgcolor: selected ? "primary.base" : "tokens.surface2",
    color: selected ? "primary.dark" : "text.disabled",
    opacity: selected ? 0.6 : 1,
    boxShadow: selected ? (theme) => `0 0 0 1px ${theme.palette.primary.main} inset` : "none",
  },
});

const cotizadorSx = (selected: boolean): SxProps<Theme> => ({
  flex: 1,
  minWidth: 84,
  p: "14px 18px",
  border: "1.5px solid",
  borderColor: selected ? "primary.accent" : "divider",
  borderRadius: "8px",
  bgcolor: selected ? "primary.base" : "background.paper",
  color: selected ? "primary.dark" : "tokens.textPrimary",
  // `ButtonBase` no hereda la tipografía del theme (no hay `CssBaseline`): se toma explícita.
  fontFamily: (theme) => theme.typography.fontFamily,
  fontSize: 13.5,
  fontWeight: selected ? 600 : 500,
  letterSpacing: "-0.005em",
  whiteSpace: "nowrap",
  transition: "border-color .18s ease, background-color .18s ease, color .18s ease",
  "&:hover": {
    borderColor: "primary.accent",
    bgcolor: "primary.base",
    // `--ld-blue-700` del diseño: el hover de la marca si lo define.
    color: (theme) => theme.palette.primary.hover ?? theme.palette.primary.main,
  },
});

/**
 * Replica `.choice-chip` de styles.css: botones rectangulares de ancho igual
 * para preguntas de opción excluyente (Sí/No, plazo, etc.).
 */
export const ChoiceToggle = ({
  value,
  onChange,
  options,
  disabled = false,
  size = "default",
  variant = "default",
  testId,
}: Props) => {
  const isCotizador = variant === "cotizador";

  return (
    <Box
      role="radiogroup"
      sx={
        isCotizador
          ? { display: "flex", gap: 1.25, flexWrap: "wrap" }
          : { display: "flex", gap: 1, flexWrap: size === "small" ? "wrap" : "nowrap" }
      }
    >
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <ButtonBase
            key={opt.value}
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            data-testid={testId ? `${testId}-${opt.value}` : undefined}
            sx={isCotizador ? cotizadorSx(selected) : defaultSx(selected, size)}
          >
            {opt.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
};
